import { SyntheticEvent, useEffect, useState } from 'react';
import { CircularProgress, Autocomplete as MuiAutocomplete, TextField } from '@mui/material';
import { CoreBox, CoreDialogContent } from '@youscience/core';
import { Roles } from '@youscience/commonapp-service-common';

import useCommonAppStore from '@store/CommonAppStore';
import useCommonAppAdminStore from '@store/CommonAppAdminStore';

import { renderNoOptionsText } from '@components/Form/Autocomplete/Autocomplete';

import DialogFooter from '@components/DialogFooter';
import { AutocompleteOption } from '@interfaces';
import { StyledDialog, StyledDialogTitle, sxStyles } from './CounselorSelection.styles';

type CounselorSelectionProps = {
  isOpenCounselorSelection: boolean;
  onCloseCounselorSelection: () => void;
};

export const CounselorSelection = ({
  isOpenCounselorSelection,
  onCloseCounselorSelection,
}: CounselorSelectionProps) => {
  const { addCounselor } = useCommonAppStore((state) => state);
  const { getRoles, roles, isLoading } = useCommonAppAdminStore((store) => store);

  const [selectedCounselor, setSelectedCounselor] = useState<{ value: string; label: string } | null>(null);
  const [nextButtonDisabled, setNextButtonDisabled] = useState<boolean>(!selectedCounselor?.value);

  useEffect(() => {
    void getRoles();
  }, []);

  useEffect(() => {
    if (selectedCounselor?.value) {
      setNextButtonDisabled(false);
    }
  }, [selectedCounselor]);

  const availableCounselors = roles
    ?.filter((role) => role.roles?.includes(Roles.COUNSELOR) && role.email && role.firstName)
    ?.map((role) => ({ value: role.userId, label: `${role.firstName} ${role.lastName}: ${role.email}` }))
    ?.sort((a, b) => a.label.localeCompare(b.label));

  const handleSelectCounselor = (event: SyntheticEvent, counselor: AutocompleteOption | null) => {
    setSelectedCounselor(counselor);
  };

  const handleConfirmSelection = async () => {
    if (!selectedCounselor?.value) return;

    await addCounselor(selectedCounselor?.value);

    onCloseCounselorSelection();
  };

  return (
    <StyledDialog open={isOpenCounselorSelection} onClose={onCloseCounselorSelection} scroll="paper">
      <StyledDialogTitle>Add your counselor</StyledDialogTitle>

      <CoreDialogContent sx={sxStyles.contentBody}>
        Please select a counselor at your school who can provide forms and recommendation for your college applications.
        <CoreBox>
          {isLoading ? <CircularProgress sx={{ position: 'absolute', left: '50%', marginLeft: '-20px' }} /> : null}
          <MuiAutocomplete
            data-testid="counselor-selection"
            sx={{ marginTop: '1.5rem ' }}
            size="small"
            options={availableCounselors || []}
            value={selectedCounselor}
            isOptionEqualToValue={(option, counselor) => option.value === counselor.value}
            onChange={handleSelectCounselor}
            renderInput={(params) => <TextField {...params} label="Counselor" />}
            clearIcon={null}
            noOptionsText={renderNoOptionsText()}
            disabled={isLoading}
          />
        </CoreBox>
      </CoreDialogContent>

      <DialogFooter
        goBackButtonTitle="Cancel"
        goNextButtonTitle="Invite"
        onGoBack={onCloseCounselorSelection}
        onGoNext={handleConfirmSelection}
        goNextActionButtonTitle="Inviting"
        isDisableNextButton={nextButtonDisabled}
      />
    </StyledDialog>
  );
};
